/**
 * folkloreDedup.ts
 *
 * The single rule for deciding whether two folklore records at the same
 * coordinate are the same legend published twice. Several sources overlap
 * geographically (Sägenkartan and Norsk Folkeminnesamling both carry parts of
 * the Norwegian material), so a coordinate can collect the same text from two
 * registers under two ids.
 *
 * Lives here because the predicate had been written out by hand in five places
 * — three in App.tsx, one in hooks/useFetchLayers.ts, one in
 * hooks/useGeoJsonLayers.ts — and a fix applied to four of them would look
 * exactly like a fix.
 */

import type { FolkloreRecord } from "../types/index.ts";

/** How much of the text has to agree before two records count as one. */
const PREFIX = 150;

/**
 * True when `rec` is already present in `existing`.
 *
 * Both records must actually carry text. Comparing on an empty text is
 * vacuous, and it was not a theoretical worry: the Danish corpus has no title
 * (the name is the generated placeholder "Folklore at <place>") and keeps its
 * text in a lazily loaded file, so every Danish record at a shared coordinate
 * compared equal to the first one and was thrown away. 900 legends became 429,
 * and the 471 that vanished were unreachable by any route in the app. Nothing
 * failed, because a pin holding one story looks like a pin holding fifteen.
 */
export function isFolkloreDuplicate(
  existing: readonly FolkloreRecord[],
  rec: FolkloreRecord,
): boolean {
  const b = (rec.text || "").slice(0, PREFIX);
  if (b === "") return false;
  return existing.some((e) => {
    const a = (e.text || "").slice(0, PREFIX);
    return a !== "" && a === b && e.name === rec.name;
  });
}
